import React from 'react';

import { ActionIcon, Text, NumberInput, TextInput, Grid, Stack } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconTrash } from '@tabler/icons-react';
import MoneyField from 'components/MoneyField';
import { useAuth } from 'context/auth-context';

import { PayRateFormValues } from './DrawerPayRateForm';

interface IPayRateTierRowProps {
  disabled: boolean;
  index: number;
  form: UseFormReturnType<PayRateFormValues, (values: PayRateFormValues) => PayRateFormValues>;
}

const PayRateTierRow = ({ disabled, index, form }: IPayRateTierRowProps): JSX.Element => {
  const { session } = useAuth();

  const payRateTiers = form.values.payRateTiers;

  const handleDeletePayRateTier = (): void => {
    form.removeListItem('payRateTiers', index);
    if (payRateTiers.length - 1) {
      const lastIndex = payRateTiers.length - 2;
      form.setFieldValue(`payRateTiers.${lastIndex}`, {
        ...payRateTiers[lastIndex],
        maxAttendants: null,
      });
    }
  };

  const handleChangeMaxAtendees = (maxAtt: number | string): void => {
    if (typeof maxAtt === 'number') {
      form.setFieldValue(`payRateTiers.${index}`, {
        ...payRateTiers[index],
        maxAttendants: maxAtt,
      });
      form.setFieldValue(`payRateTiers.${index + 1}`, {
        ...payRateTiers[index + 1],
        minAttendants: maxAtt + 1,
      });
    }
  };

  return (
    <Stack>
      <Grid align='center' justify='center' gutter='xs' columns={12}>
        <Grid.Col span={2}>
          <NumberInput
            size='xs'
            min={index >= 1 ? (payRateTiers[index - 1].maxAttendants || 0) + 1 : 1}
            max={
              payRateTiers[index].maxAttendants ? (payRateTiers[index].maxAttendants || 1) - 1 : undefined
            }
            disabled
            {...form.getInputProps(`payRateTiers.${index}.minAttendants`)}
          />
        </Grid.Col>
        <Grid.Col span={1}>
          <Text size='xs'>to</Text>
        </Grid.Col>
        <Grid.Col span={2}>
          {payRateTiers[index].maxAttendants === null ? (
            <TextInput value='&infin;' disabled size='xs' />
          ) : (
            <NumberInput
              size='xs'
              disabled={
                disabled || (index === payRateTiers.length - 1 && payRateTiers[index].maxAttendants === null)
              }
              {...form.getInputProps(`payRateTiers.${index}.maxAttendants`)}
              onChange={handleChangeMaxAtendees}
              value={payRateTiers[index].maxAttendants || undefined}
              min={payRateTiers[index].minAttendants}
              max={
                payRateTiers.length - 2 === index || payRateTiers.length - 1 === index
                  ? undefined
                  : payRateTiers[index + 1].minAttendants - 1
              }
            />
          )}
        </Grid.Col>
        <Grid.Col span={1}>
          <Text size='xs'>pays</Text>
        </Grid.Col>
        <Grid.Col span={3}>
          <MoneyField
            country={session?.gym?.country}
            data-cy='payRateTierAmountNumberInput'
            size='xs'
            min={0}
            disabled={disabled}
            {...form.getInputProps(`payRateTiers.${index}.amount`)}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <Text size='xs'>per attendee</Text>
        </Grid.Col>

        <Grid.Col span={1}>
          {index === payRateTiers.length - 1 && (
            <ActionIcon
              onClick={handleDeletePayRateTier}
              disabled={disabled || payRateTiers.length === 1}
              data-cy='deletePayRateTierButton'
            >
              <IconTrash size='1.2rem' />
            </ActionIcon>
          )}
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

export default PayRateTierRow;
